/**
 * Module dependencies,
 */

import { BeforeAfterSlider } from 'src/components/layout/service/before-after-slider';
import { Breadcrumbs } from 'src/components/core/breadcrumbs';
import { Button } from 'src/components/core/buttons/button';
import { CardSlide } from 'src/components/core/carousel';
import { Container } from 'src/components/core/layout/container';
import { DetailedService } from 'src/types/service';
import { GalleryCarousel } from 'src/components/layout/section/gallery-carousel';
import { GetServerSideProps, NextPage } from 'next';
import { GridHeader } from 'src/components/layout/service/grid-header';
import { HorizontalTextBlocks } from 'src/components/layout/service/horizontal-text-blocks';
import { IconCardsGrid } from 'src/components/layout/grid/icon-cards';
import { Locale } from 'i18n-routes';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { Testimonials } from 'src/components/layout/section/testimonials';
import { Text } from 'src/components/core/text';
import { TriggerNavbarTheme } from 'src/components/layout/navbar/trigger-navbar-theme';
import { VerticalTextBlocks } from 'src/components/layout/service/vertical-text-blocks';
import { getLocalizedCategoryByServiceSlug } from 'src/core/utils/routes';
import { getService } from 'src/requests/service';
import { media } from 'src/styles/utils/media';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { textGradient } from 'src/styles/gradients';
import { useCallback, useMemo } from 'react';
import { useRouteResolver } from 'src/hooks/use-route-resolver';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import buttonIcon from 'src/assets/svg/button.svg';
import camelCase from 'lodash/camelCase';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: {
    locale: Locale;
    service: DetailedService;
  };
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.section`
  color: var(--color-gray700);
`;

/**
 * `Header` styled component.
 */

const Header = styled.div`
  padding-top: calc(var(--navbar-height) + 3rem);

  ${media.min.ms`
    padding-top: calc(var(--navbar-height) + 5rem);
  `}
`;

/**
 * `PaddedContainer` styled component.
 */

const PaddedContainer = styled(Container)`
  padding-bottom: var(--vertical-spacing);
  padding-top: var(--vertical-spacing);
`;

/**
 * `StyledVerticalTextBlocks` styled component.
 */

const StyledVerticalTextBlocks = styled(VerticalTextBlocks)`
  padding-top: var(--vertical-spacing);
`;

/**
 * `FlexColumn` styled component.
 */

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * `GradientText` styled component.
 */

const GradientText = styled(Text).attrs({
  block: true,
  variant: 'display2'
})`
  text-align: center;

  &::first-letter {
    text-transform: uppercase;
  }

  ${textGradient}
`;

/**
 * `ServiceDetail` page.
 */

const ServiceDetail: NextPage<Props> = ({ data }) => {
  const service = data?.service;
  const { category, slug, subCategory, title } = service;
  const { locale } = useRouter();
  const { t } = useTranslation(['common', 'service', 'what-we-do']);
  const resolveRoute = useRouteResolver();
  const getServiceBreadcrumbs = useCallback(
    (category: string, hideLinks?: boolean) => [
      {
        label: t('common:terms.services'),
        url: hideLinks ? undefined : resolveRoute('allServices')
      },
      {
        label: t(`common:service.category.${category}`),
        url: hideLinks
          ? undefined
          : resolveRoute('serviceType', {
            slug: getLocalizedCategoryByServiceSlug(
                locale as Locale,
                category
            )
          })
      }
    ],
    [locale, resolveRoute, t]
  );

  const breadcrumbs = useMemo(
    () => [
      ...getServiceBreadcrumbs(category as any),
      {
        label: t(`common:service.subCategory.${camelCase(subCategory)}`),
        url: resolveRoute('services', {
          subCategory: getLocalizedCategoryByServiceSlug(
            locale as Locale,
            subCategory
          )
        })
      },
      {
        label: title
      }
    ],
    [
      category,
      getServiceBreadcrumbs,
      locale,
      resolveRoute,
      subCategory,
      t,
      title
    ]
  );

  return (
    <>
      <SEO
        description={service.descriptionSeo}
        openGraph={{
          description: service.descriptionSeo,
          locale: data.locale,
          title: service.titleSeo
        }}
        title={service.titleSeo}
      />

      <Wrapper>
        <TriggerNavbarTheme theme={'light'} />

        <Header>
          <Container>
            <Breadcrumbs
              data-scroll={'fade-in-up'}
              items={breadcrumbs}
            />

            <GridHeader
              buttonLabel={service?.linkLabel || t('common:actions.makeAnAppointment')}
              buttonLink={resolveRoute('serviceAppointment', {
                slug: service.slug
              })}
              description={service.description}
              images={[service.image1, service.image2]}
              price={service.price}
              priceDefaultLabel={t('service:detail.withoutPrice')}
              secondDescription={service?.whatIsDescription}
              secondTitle={service?.whatIsTitle}
              title={title}
            />

            <HorizontalTextBlocks
              blocks={[
                {
                  description: service.benefitsDescription,
                  title: service.benefitsTitle
                },
                {
                  description: service.whoItIsForDescription,
                  title: service.whoItIsForTitle
                }
              ]}
              offsetTitle
            />
          </Container>
        </Header>

        {!!service.howWeDoItems.length && (
          <>
            <TriggerNavbarTheme theme={'dark'} />

            <IconCardsGrid
              items={service.howWeDoItems}
              title={t('what-we-do:howWeDoIt.title')}
            />

            <TriggerNavbarTheme theme={'light'} />
          </>
        )}

        <StyledVerticalTextBlocks
          blocks={[
            {
              description: service.recoveryDescription,
              title: service.recoveryTitle
            },
            {
              description: service.resultsDescription,
              title: service.resultsTitle
            }
          ]}
        />

        {!!service.resultImage1 && !!service.resultImage2 && (
          <Container>
            <BeforeAfterSlider
              images={[service.resultImage1, service.resultImage2]}
            />
          </Container>
        )}

        <Testimonials
          hideBackgroundCircles
          items={service.testimonials}
        />

        <PaddedContainer>
          <FlexColumn>
            {service.price && (
              <Text
                data-scroll={'fade-in-up'}
                style={{ textAlign: 'center' }}
                variant={'subtitle2'}
              >
                {service.price}
              </Text>
            )}

            <GradientText
              data-scroll={'fade-in-up'}
              data-scroll-offset={'20%'}
            >
              {t('common:actions.bookConsultation')}
            </GradientText>

            <Button
              colorTheme={'green'}
              data-scroll={'fade-in-up'}
              data-scroll-animation-delay={100}
              data-scroll-offset={'20%'}
              href={resolveRoute('serviceAppointment', { slug })}
              icon={buttonIcon}
              style={{
                alignSelf: 'center',
                justifySelf: 'center',
                marginTop: '2.5rem'
              }}
            >
              {!service.linkLabel
                ? t('common:actions.makeAnAppointment')
                : service.linkLabel}
            </Button>
          </FlexColumn>
        </PaddedContainer>

        {!!service.relatedServicesCategory.length && (
          <GalleryCarousel
            buttonLabel={t('service:detail.related.viewAll')}
            buttonUrl={resolveRoute('allServices')}
            slidesLength={service.relatedServicesCategory.length}
            title={t('service:detail.related.title')}
          >
            {service.relatedServicesCategory.map((item, index) => (
              <CardSlide
                data-scroll={'clip-left'}
                data-scroll-animation-delay={400 * index}
                data-scroll-offset={'20%'}
                href={resolveRoute('service', { slug: item.slug })}
                imageUrl={item.highlightImage}
                key={item.slug}
                lead={(
                  <Breadcrumbs
                    items={getServiceBreadcrumbs(item.category, true)}
                  />
                )}
                title={item.title}
              />
            ))}
          </GalleryCarousel>
        )}
      </Wrapper>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps: GetServerSideProps<
  SSGProps
> = async context => {
  try {
    const { locale, params } = context;
    const service = await getService(params?.slug as string, locale as Locale);

    if (!service) {
      throw new Error('notFound');
    }

    return {
      props: {
        data: { locale, service },
        navbarTheme: 'light',
        ...(await serverSideTranslations(locale as Locale, [
          'common',
          'what-we-do',
          'service'
        ]))
      }
    };
  } catch (error) {
    return {
      notFound: true
    };
  }
};

/**
 * Export `ServiceDetail` component.
 */

export default ServiceDetail;
